import LinkButton from '@components/utils/cta/linkButton';
import { Caption } from '@components/utils/text';
import { COMMON } from '@constants';
import { css, Theme } from '@emotion/react';
import styled from '@emotion/styled';
import {
    OutboundLinkContext,
    OutboundLinkDestinationCategory,
    OutboundLinkShape,
} from '@enums';
import {
    AdditionalProductFragment,
    RedirectionType,
} from '@graphql/generated/graphql';
import { useCompanyConfig } from '@lib/config/hooks';
import StrapiImage from '@lib/image/strapiImage';
import { getStrapiAssetUrl } from '@lib/strapi';
import { VStack } from '@lib/uikit';
import { useTranslation } from 'next-i18next';

type Props = { additionalProduct: AdditionalProductFragment };

const HStack = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.md};
`;

const imageStyle = ({ theme }: { theme: Theme }) => css`
    object-fit: cover;
    border-radius: ${theme.shape.radius.default};
    width: 8rem;
    height: 8rem;
`;

const SStrapiImage = styled(StrapiImage)`
    ${imageStyle}
`;

const StyledImage = styled.img`
    ${imageStyle}
`;

const Title = styled.p`
    font: ${({ theme }) => theme.fonts.heading3};
    color: ${({ theme }) => theme.palette.text.primary};
`;

const SLinkButton = styled(LinkButton)`
    margin-top: ${({ theme }) => theme.spacing.xs};
`;

export const AdditionalProductModalPreview = ({ additionalProduct }: Props) => {
    const { t } = useTranslation(COMMON);
    const { additionalProduct: additionalProductRenderOptions } =
        useCompanyConfig();

    if (!additionalProduct.Url) return null;

    return (
        <HStack>
            {additionalProduct.ImageUrl ? (
                <StyledImage
                    src={additionalProduct.ImageUrl}
                    alt={additionalProduct.Name}
                />
            ) : additionalProduct.Image ? (
                <SStrapiImage
                    sizes='8rem'
                    src={getStrapiAssetUrl(additionalProduct.Image)}
                    formats={additionalProduct.Image.formats}
                    alt={
                        additionalProduct.Image.alternativeText ??
                        additionalProduct.Name
                    }
                />
            ) : null}
            <VStack spacing='xxs'>
                <Title>{additionalProduct.Name}</Title>
                {additionalProductRenderOptions.withDescription.inDetail &&
                    additionalProduct.Description && (
                        <Caption>{additionalProduct.Description}</Caption>
                    )}
                <SLinkButton
                    type={RedirectionType.Outside}
                    url={additionalProduct.Url}
                    text={t('purchase')}
                    destinationName={additionalProduct.Name}
                    destinationCategory={
                        OutboundLinkDestinationCategory.AdditionalProduct
                    }
                    context={OutboundLinkContext.Modal}
                    shape={OutboundLinkShape.Button}
                    buttonSize='xsmall'
                />
            </VStack>
        </HStack>
    );
};
